<template>
  <content-container :overlay="false">
    <new-email-modal
      ref="new-email-modal-ctrl"
      :current="
        $store.state.user.emails.length ? $store.state.user.emails[0].email : ''
      "
    ></new-email-modal>

    <!-- Email --------------------------------------------------------------------------->
    <simple-card :loading="loading" id="user-email">
      <template v-slot:title>
        {{ $t("profile.comm.email.title") }}
      </template>
      <template v-slot:description>
        <div v-if="$store.state.user.emails.length">
          <div
            class="email"
            v-for="email in $store.state.user.emails"
            :key="email.id"
          >
            {{ email.email }}
            <span class="text-success" v-if="email.verified"
              >({{ $t("profile.comm.email.verified") }})</span
            >
            <span class="text-danger" v-else
              >({{ $t("profile.comm.email.unverified") }})</span
            >
            <!-- Перепосылка кода --------------->
            <div class="ml-3 d-inline-block">
              <b-overlay
                :show="resending"
                rounded
                opacity="0.7"
                spinner-small
                spinner-variant="light"
                class="d-inline-block"
              >
                <b-button
                  v-if="!email.verified"
                  size="sm"
                  variant="success"
                  @click="resendClick(email)"
                  :disabled="!canResend && !resending"
                >
                  <template v-if="canResend">
                    {{ $t("profile.comm.email.resend") }}
                  </template>
                  <template v-else>
                    {{ $t("verification.nextIn", [remaining]) }}
                  </template>
                </b-button>
              </b-overlay>

              <!-- Удаление не проверенного -------->
              <b-overlay
                :show="deleting"
                rounded
                opacity="0.7"
                spinner-small
                spinner-variant="light"
                class="d-inline-block"
              >
                <b-button
                  v-if="!email.verified"
                  size="sm"
                  variant="danger"
                  class="ml-3"
                  :disabled="deleting"
                  @click="deleteClick(email)"
                  ><b-icon-x></b-icon-x
                ></b-button>
              </b-overlay>
            </div>
          </div>
        </div>
        <span class="text-danger" v-else>{{
          $t("profile.comm.email.empty")
        }}</span>
      </template>
      <template v-slot:pane>
        <!-- Изменить текущий или установить адрес почты впервые -->
        <b-button
          v-if="$store.state.user.emails.length"
          variant="primary"
          @click="showChangeEmail()"
        >
          {{ $t("profile.comm.email.change_nice") }}
        </b-button>
        <b-button v-else variant="danger" @click="showNewEmail()">{{
          $t("profile.comm.email.set")
        }}</b-button>
      </template>
    </simple-card>

    <!-- Language --------------------------------------------------------------------------->
    <simple-card :loading="loading">
      <template v-slot:title>
        {{ $t("profile.comm.lang.title") }}
      </template>
      <template v-slot:description>
        {{ $t("profile.comm.lang.desc") }}
      </template>
      <template v-slot:pane>
        <b-form-select v-model="curLocale" @change="onLocaleChange">
          <b-form-select-option
            v-for="(item, index) in locales"
            :key="index"
            :value="item.code"
            >{{ item.name }}
          </b-form-select-option>
        </b-form-select>
      </template>
    </simple-card>

    <!-- Communication Preferences ------------------------------------------------------------->
    <collapsible-card
        ref="name"
        :loading="loading || saveCommPrefsRequest"
        :changed="isCommPrefsChanged()"
        no-close-on-ok
        @click="handleSaveCommPrefs()"
    >
      <template v-slot:title>
        {{ $t("profile.comm.pref.title") }}
      </template>
      <template v-slot:description>
        {{ $t("profile.comm.pref.none", [$t("app.company")]) }}
      </template>
      <template v-slot:content>
        <div class="mt-3 mb-3">
          preferences:
        </div>
        <b-form-group
          id="fieldset-1"
          :description="$t('profile.comm.pref.blog.desc')"
          label-for="blog-cb"
        >
          <b-form-checkbox
            id="blog-cb"
            name="blog"
            switch
            v-model="preferences.mail_blog"
          >
            {{ $t("profile.comm.pref.blog.title") }}
          </b-form-checkbox>
        </b-form-group>

        <b-form-group
          id="fieldset-2"
          :description="$t('profile.comm.pref.events.desc')"
          label-for="events-cb"
        >
          <b-form-checkbox
            id="events-cb"
            name="events"
            switch
            v-model="preferences.mail_events"
          >
            {{ $t("profile.comm.pref.events.title") }}
          </b-form-checkbox>
        </b-form-group>

        <b-form-group
          id="fieldset-3"
          :description="$t('profile.comm.pref.promo.desc')"
          label-for="promo-cb"
        >
          <b-form-checkbox
            id="promo-cb"
            name="promos"
            switch
            v-model="preferences.mail_promotion"
          >
            {{ $t("profile.comm.pref.promo.title") }}
          </b-form-checkbox>
        </b-form-group>
      </template>
    </collapsible-card>
  </content-container>
</template>

<script>
import ContentContainer from "@/components/ContentContainer";
import { getSupportedLocales } from "@/util/supported-locales";
import SimpleCard from "@/components/SimpleCard";
import CollapsibleCard from "@/components/CollapsibleCard";
import NewEmailModal from "@/components/user-profile/NewEmailModal";
import store from "@/store/";
import { SET_LOCALE, SETUP_FROM_DATA, DELETE_EMAIL } from "@/store/consts";
import { BIconX } from "bootstrap-vue";
import {
  RESEND_EMAIL_INTERVAL_SECS,
  URL_RESEND_CONFIRMATION,
  URL_DELETE_USER_EMAIL,
  URL_LOAD_USER,
  URL_CHANGE_COMM_PREFS
} from "@/globals";
import * as errs from "@/util/errors";

export default {
  name: "Profile",
  components: {
    ContentContainer,
    SimpleCard,
    CollapsibleCard,
    NewEmailModal,
    BIconX,
  },
  data: () => ({
    loading: false,
    saveCommPrefsRequest: false,
    resending: false,
    deleting: false,
    locales: getSupportedLocales(),
    curLocale: store.state.user.locale,
    preferences: {
      mail_blog: false,
      mail_events: false,
      mail_promotion: false,
    },
    showEmailModal: false,
    resendCounter: RESEND_EMAIL_INTERVAL_SECS,
  }),
  computed: {
    remaining: function () {
      return Math.floor(RESEND_EMAIL_INTERVAL_SECS - this.resendCounter);
    },
    canResend: function () {
      return this.resendCounter >= RESEND_EMAIL_INTERVAL_SECS;
    },
  },
  methods: {
    isCurrentLocale(code) {
      return code === store.state.user.locale;
    },
    onLocaleChange: function (code) {
      this.$store.dispatch(SET_LOCALE, code);
    },
    showChangeEmail: function () {
      this.$refs["new-email-modal-ctrl"].show();
      //this.showEmailModal = true
    },
    showNewEmail: function () {
      this.$refs["new-email-modal-ctrl"].show();
      //this.showEmailModal = true
    },
    resendClick: function (email) {
      this.resending = true;
      this.$post(URL_RESEND_CONFIRMATION.format(email.id), {}, true, false)
        .then(() => {
          this.resetResendTimeout(0);
          this.resending = false;
        })
        .catch((e) => {
          this.resending = false;
          switch (e.code) {
            case errs.CODE_ACC_TO_FAST:
              this.$alert.show(this.$t("profile.comm.email.tooFast"));
              break;
            default:
              this.$alert.show(e);
              break;
          }
        });
    },
    deleteClick: function (email) {
      this.deleting = true;
      this.$delete(URL_DELETE_USER_EMAIL, { id: email.id }, true, false)
        .then(() => {
          this.$store.commit(DELETE_EMAIL, email.id);
          this.deleting = false;
        })
        .catch((e) => {
          this.deleting = false;
          this.$alert.show(e);
        });
    },
    updateResendCounter() {
      this.resendCounter++;
      if (this.resendCounter >= RESEND_EMAIL_INTERVAL_SECS) {
        clearInterval(this.timerId);
      }
    },
    resetResendTimeout(elapsed) {
      this.resendCounter = elapsed;
      this.timerId = setInterval(() => {
        this.updateResendCounter();
      }, 1000);
    },
    isCommPrefsChanged() {
      return this.preferences.mail_blog != this.$store.state.user.settings.mail_blog ||
          this.preferences.mail_events != this.$store.state.user.settings.mail_events ||
          this.preferences.mail_promotion != this.$store.state.user.settings.mail_promotion;
    },
    handleSaveCommPrefs() {
      this.$patch(URL_CHANGE_COMM_PREFS, {
        mail_blog: this.preferences.mail_blog,
        mail_events: this.preferences.mail_events,
        mail_promotion: this.preferences.mail_promotion
      }, true, 'saveCommPrefsRequest', true)
          .then((result) => {
            // Обновим адрес клиента
            this.$store.commit('comm_prefs', result.mail_blog, result.mail_events, result.mail_promotion);
            this.$refs.name.close();
          })
          .catch((e) => {
            this.$alert.show(e);
          });
    }
  },
  created() {
    // Токен должен быть свежим, перечитаем пользователя с сервера
    this.$get(URL_LOAD_USER, true)
      .then((result) => {
        // Обновим экземпляр пользователя
        this.$store.dispatch(SETUP_FROM_DATA, result);
        this.preferences = {
          mail_blog: this.$store.state.user.settings.mail_blog,
          mail_events: this.$store.state.user.settings.mail_events,
          mail_promotion: this.$store.state.user.settings.mail_promotion,
        };
      })
      .catch((e) => {
        // Странный случай, предложим войти в кабинет
        this.$router.push({ name: "sign-in" });
        this.$alert.show(e);
      });
  },
};
</script>

<style scoped>
.email:not(:first-child) {
  margin-top: 0.25em;
}
</style>